import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { companyAPI } from '../../lib/api';
import { DatePickerDE } from '../../components/ui/date-picker-de';
import { Plus, Trash2, Loader2, CalendarOff } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import { toast } from 'sonner';

const isoToDe = (iso) => {
  try {
    return format(parseISO(iso), 'EEE, dd.MM.yyyy', { locale: de });
  } catch {
    return iso;
  }
};

export default function ClosureDaysManager() {
  const [closures, setClosures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ date: '', name: '' });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await companyAPI.listClosures();
      setClosures(res.data || []);
    } catch {
      toast.error('Schließtage konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!form.date || !form.name.trim()) {
      toast.error('Bitte Datum und Bezeichnung angeben');
      return;
    }
    try {
      setSaving(true);
      await companyAPI.createClosure({ date: form.date, name: form.name.trim() });
      toast.success('Schließtag hinzugefügt');
      setForm({ date: '', name: '' });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Schließtag konnte nicht angelegt werden');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await companyAPI.deleteClosure(id);
      toast.success('Schließtag gelöscht');
      setClosures((prev) => prev.filter((c) => c.id !== id));
    } catch {
      toast.error('Löschen fehlgeschlagen');
    }
  };

  return (
    <Card data-testid="closure-days-manager">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CalendarOff className="h-5 w-5" />
          Interne Schließtage / Brückentage
        </CardTitle>
        <CardDescription>
          Firmenweite betriebsfreie Tage (z. B. Brückentage, Betriebsruhe). An diesen Tagen wird
          bei der Serien-Anlage von Einsätzen kein Einsatz erstellt und der Tag im Einsatzplan rot markiert.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
          <div className="space-y-2 sm:w-48">
            <Label>Datum</Label>
            <DatePickerDE
              value={form.date}
              onChange={(v) => setForm((p) => ({ ...p, date: v }))}
              placeholder="Datum wählen"
              testId="closure-date-input"
            />
          </div>
          <div className="space-y-2 flex-1">
            <Label>Bezeichnung</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              placeholder="z. B. Brückentag, Betriebsausflug"
              data-testid="closure-name-input"
            />
          </div>
          <Button
            onClick={handleAdd}
            disabled={saving}
            className="rounded-full"
            data-testid="add-closure-btn"
          >
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
            Hinzufügen
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Wird geladen …
          </div>
        ) : closures.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="closure-empty">
            Noch keine Schließtage hinterlegt.
          </p>
        ) : (
          <div className="rounded-lg border divide-y" data-testid="closure-list">
            {closures.map((c) => (
              <div
                key={c.id}
                className="flex items-center justify-between px-4 py-2.5"
                data-testid={`closure-row-${c.id}`}
              >
                <div>
                  <span className="font-medium">{isoToDe(c.date)}</span>
                  <span className="ml-2 text-sm text-muted-foreground">{c.name}</span>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDelete(c.id)}
                  className="text-destructive hover:text-destructive"
                  data-testid={`delete-closure-${c.id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
